import PatientRowName from 'components/Misc/PatientRowName';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Row,
  Table,
} from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import ConversationRowName from './ConversationRowName';

function DynamicSearchBox({ styleType }) {
  // Get our Globals
  const { apiClient } = useContext(GlobalContext);

  const navigate = useNavigate();

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  // Conversations fetched with 1-year lookback for unfiltered search
  const [searchConversations, setSearchConversations] = useState(null);
  const fetchedRef = useRef(false);

  // Re-filter with the expanded dataset once it loads
  useEffect(() => {
    if (searchQuery && searchConversations !== null) {
      setFilteredConversations(
        searchConversations
          .filter((c) =>
            c.conversationName.toLowerCase().includes(searchQuery.toLowerCase()),
          )
          .slice(0, 5),
      );
    }
  }, [searchConversations]);

  // Fetch 1-year lookback when the search box is focused
  const handleFocus = () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

    apiClient
      .getUserConversations(null, oneYearAgo.toISOString(), new Date().toISOString())
      .then((data) => {
        setSearchConversations(
          data && !data.error && data.conversations
            ? data.conversations
            : apiClient.conversations,
        );
      })
      .catch(() => setSearchConversations(apiClient.conversations));
  };

  // Prevent default form submission
  const handleSubmit = (e) => {
    e.preventDefault();
  };

  // Function to handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Filter conversations based on conversationName
    const conversationsToSearch = searchConversations ?? apiClient.conversations;
    const filteredConvos = conversationsToSearch
      .filter((conversation) =>
        conversation.conversationName
          .toLowerCase()
          .includes(query.toLowerCase()),
      )
      .slice(0, 5);

    // Filter patients based on patientName
    const filteredPats = apiClient.patients
      .filter(
        (patient) =>
          String(patient.lastName || '')
            .toLowerCase()
            .includes(query.toLowerCase()) ||
          String(patient.firstName || '')
            .toLowerCase()
            .includes(query.toLowerCase()),
      )
      .slice(0, 5);

    setFilteredConversations(filteredConvos);
    setFilteredPatients(filteredPats);
  };

  const formClass =
    styleType === 'mobile'
      ? 'navbar-search navbar-search-light form-inline mr-3 ml-lg-auto'
      : 'navbar-search navbar-search-dark form-inline mr-3 d-none d-md-flex ml-lg-auto';

  return (
    <Form className={formClass} onSubmit={handleSubmit}>
      <FormGroup id="dynamic-search-box" className="mb-0">
        <InputGroup className="input-group-alternative">
          <InputGroupAddon addonType="prepend">
            <InputGroupText>
              <i className="fas fa-search" />
            </InputGroupText>
          </InputGroupAddon>
          <Input
            placeholder="Search"
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={handleFocus}
            style={styleType === 'mobile' ? { width: '60%' } : undefined}
          />
        </InputGroup>
      </FormGroup>
      {/* Render filtered results below the search input */}
      {searchQuery !== '' && (
        <div
          className="search-results"
          style={{
            position: 'fixed',
            top: '100px',
            left: '8px',
            right: '8px',
            height: 'calc(100vh - 70px)',
            zIndex: 1000,
            overflowY: 'auto',
            background: 'white',
            maxWidth: '600px',
            margin: '0 auto',
            borderRadius: '8px',
          }}
        >
          <Card
            className="shadow"
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '8px',
              border: 'none',
            }}
          >
            {/* Render the patients first */}
            {filteredPatients.length > 0 && (
              <>
                <CardHeader className="border-0">
                  <Row className="align-items-center">
                    <div className="col">
                      <h3 className="mb-0">Patients</h3>
                    </div>
                    <div className="col text-right"></div>
                  </Row>
                </CardHeader>
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Patient name</th>
                      <th scope="col">Visits</th>
                      <th scope="col">Last Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatients.map((patient) => (
                      <tr
                        key={patient.patientName}
                        onClick={() => {
                          navigate(`/User/conversations#${patient.uid}`);
                          setSearchQuery('');
                        }}
                        className="clickable-row"
                      >
                        <PatientRowName
                          fName={patient.firstName}
                          lName={patient.lastName}
                          dob={patient.dob}
                        />
                        <td>
                          {patient.conversationIds
                            ? patient.conversationIds.length
                            : null}
                        </td>
                        <td>{patient.lastUsed}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {filteredPatients.length > 0 &&
              filteredConversations.length > 0 && (
                <hr
                  style={{
                    border: 'none',
                    borderTop: '8px solid #f7f7f7',
                    margin: '0px 0', // Adjust margin as needed
                  }}
                />
              )}

            {filteredConversations.length > 0 && (
              <>
                <CardHeader className="border-0">
                  <Row className="align-items-center">
                    <div className="col">
                      <h3 className="mb-0">Conversations</h3>
                    </div>
                    <div className="col text-right"></div>
                  </Row>
                </CardHeader>
                <Table className="align-items-center table-flush" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th scope="col">Name</th>
                      <th scope="col">Last Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredConversations.map((conversation) => (
                      <tr
                        key={conversation.conversationId}
                        onClick={() => {
                          navigate(
                            `/User/conversations/details#${conversation.conversationId}`,
                          );
                          setSearchQuery('');
                        }}
                        className="clickable-row"
                      >
                        <ConversationRowName conversation={conversation} />
                        <td>{conversation.lastUsed}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {filteredPatients.length === 0 &&
              filteredConversations.length === 0 && (
                <>
                  <CardHeader className="border-0">
                    <Row className="align-items-center">
                      <div className="col">
                        <h3 className="mb-0">No results found</h3>
                      </div>
                      <div className="col text-right"></div>
                    </Row>
                  </CardHeader>
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th scope="col">Patient name</th>
                        <th scope="col">Visits</th>
                        <th scope="col">Last Used</th>
                      </tr>
                    </thead>
                  </Table>
                </>
              )}
          </Card>
        </div>
      )}
    </Form>
  );
}

export default DynamicSearchBox;
